"use client";

import { useState, useEffect } from "react";
import Image from "next/image";
import Link from "next/link";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, ArrowDown, X, Menu } from "lucide-react";
import ContactPanel from "./ContactPanel";

const INDEX = [
  { id: "work", num: "01", label: "Selected Work" },
  { id: "pricing", num: "02", label: "Rates" },
  { id: "process", num: "03", label: "Process & AI" },
  { id: "clients", num: "04", label: "Clients" },
  { id: "about", num: "05", label: "About" },
  { id: "faq", num: "06", label: "FAQ" },
  { id: "contact", num: "07", label: "Start a Project" },
];

const MARQUEE = [
  "Custom Code",
  "Next.js",
  "React",
  "Claude",
  "GPT",
  "Gemini",
  "SEO",
  "Pasadena, CA",
  "No Templates",
  "No Page Builders",
];

const PROCESS = [
  {
    step: "Discovery",
    detail:
      "A conversation about your business, your customers, and what the site needs to accomplish. You get a plain-language plan and a fixed quote.",
  },
  {
    step: "Design",
    detail:
      "I design directly around your content, not a template. You see the direction early and we adjust before anything is built.",
  },
  {
    step: "Build",
    detail:
      "Hand-written code, tested on real devices, tuned for speed and search. You own 100% of it when we are done.",
  },
  {
    step: "Launch & support",
    detail:
      "A guaranteed launch date, then optional hosting and care so the site stays fast, secure, and up to date.",
  },
];

const AI_STACK = [
  {
    task: "Architecture & production code",
    model: "Claude",
    maker: "Anthropic",
    detail: "Complex builds, refactors, and code review on every project.",
  },
  {
    task: "Research & content groundwork",
    model: "GPT",
    maker: "OpenAI",
    detail: "Competitive research, copy drafts, and SEO groundwork.",
  },
  {
    task: "Visual & long-context analysis",
    model: "Gemini",
    maker: "Google",
    detail: "Design audits against screenshots and large-document review.",
  },
  {
    task: "Direction, taste & the final call",
    model: "Me",
    maker: "Human, Pasadena",
    detail: "AI drafts, I decide. Everything ships human-reviewed.",
  },
];

const FAQ_ITEMS = [
  {
    q: "What does a website cost?",
    a: "Landing pages start at $500. Full custom websites start at $2,500. Managed hosting and care is $100 per month. You get a fixed quote before any work starts, and the rate card above is the real one.",
  },
  {
    q: "Do you use templates or page builders?",
    a: "No. Every site is designed and coded from scratch around your business. That is why the sites are fast, and why you can take the code anywhere.",
  },
  {
    q: "Do I own the website?",
    a: "Yes, 100%. The code, the design, the content. No lock-in, no platform fees, no hostage situations.",
  },
  {
    q: "How do you use AI?",
    a: "Openly. I direct frontier models, Claude for code, GPT for research and content groundwork, Gemini for visual and long-context analysis. It makes one designer move like a team. Every deliverable is reviewed, tested, and shipped by me personally.",
  },
  {
    q: "How fast will I hear back?",
    a: "Within 24 hours, usually sooner. Every project gets a guaranteed launch date in writing.",
  },
];

const formatPrice = (service) => {
  const price =
    service.isDiscounted && service.discountedPrice
      ? service.discountedPrice
      : service.originalPrice;
  if (!price || price.trim() === "") return "Custom";
  return price.includes("$") ? price : `$${price}`;
};

function useActiveSection() {
  const [active, setActive] = useState("");

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) setActive(entry.target.id);
        });
      },
      { rootMargin: "-35% 0px -60% 0px" }
    );

    INDEX.forEach(({ id }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, []);

  return active;
}

function SectionHeading({ num, title, note }) {
  return (
    <div className="mb-12 relative z-10">
      <p className="eyebrow rule mb-5">
        <span>
          <span className="text-[var(--brand-color)]">{num}</span> · {title}
        </span>
      </p>
      {note && <p className="text-[var(--text-secondary)] max-w-xl">{note}</p>}
    </div>
  );
}

const reveal = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.6, ease: [0.22, 1, 0.36, 1] },
};

const fadeUp = (delay = 0) => ({
  initial: { opacity: 0.001, y: 28 },
  animate: { opacity: 1, y: 0 },
  transition: { delay, duration: 0.7, ease: [0.22, 1, 0.36, 1] },
});

export default function StudioHome({ projects, services, testimonials }) {
  const active = useActiveSection();
  const [menuOpen, setMenuOpen] = useState(false);

  // Lock page scroll while the mobile index overlay is open
  useEffect(() => {
    document.documentElement.style.overflow = menuOpen ? "hidden" : "";
    return () => {
      document.documentElement.style.overflow = "";
    };
  }, [menuOpen]);

  const sortedProjects = [...projects].sort((a, b) => {
    if (a.isDemo !== b.isDemo) return a.isDemo ? 1 : -1;
    return (a.order || 0) - (b.order || 0);
  });

  const packages = services.filter(
    (s) => s.displayCategory === "Website Packages"
  );
  const support = services.filter(
    (s) => s.displayCategory === "Ongoing Support"
  );

  const railLink = (item) =>
    `flex items-baseline gap-3 py-1.5 pl-3 border-l font-mono text-xs uppercase tracking-[0.16em] transition-all duration-300 ${
      active === item.id
        ? "border-[var(--brand-color)] text-[var(--brand-color)] translate-x-1"
        : "border-transparent text-[var(--text-muted)] hover:text-[var(--text-primary)]"
    }`;

  return (
    <div
      id="top"
      className="min-h-screen bg-[var(--bg-primary)] text-[var(--text-primary)]"
    >
      {/* Mobile top bar: single row, index opens as full-screen overlay */}
      <header className="lg:hidden sticky top-0 z-50 bg-[var(--bg-primary)]/95 backdrop-blur-sm border-b border-[var(--border-color)]">
        <div className="px-5 py-4 flex items-center justify-between">
          <a href="#top" onClick={() => setMenuOpen(false)}>
            <span className="font-display text-lg font-semibold">behes</span>
            <span className="font-mono text-sm text-[var(--brand-color)]">
              .dev
            </span>
          </a>
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-[var(--text-secondary)] border border-[var(--border-light)] rounded-sm px-4 py-2"
            aria-label="Open page index"
            aria-expanded={menuOpen}
          >
            Index <Menu className="w-4 h-4 text-[var(--brand-color)]" />
          </button>
        </div>
      </header>

      {/* Mobile index overlay */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            className="lg:hidden fixed inset-0 z-[90] bg-[var(--bg-primary)] flex flex-col"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            role="dialog"
            aria-modal="true"
            aria-label="Page index"
          >
            <div className="px-5 py-4 flex items-center justify-between border-b border-[var(--border-color)]">
              <span>
                <span className="font-display text-lg font-semibold">
                  behes
                </span>
                <span className="font-mono text-sm text-[var(--brand-color)]">
                  .dev
                </span>
              </span>
              <button
                type="button"
                onClick={() => setMenuOpen(false)}
                className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-[var(--text-secondary)] border border-[var(--border-light)] rounded-sm px-4 py-2"
                aria-label="Close page index"
              >
                Close <X className="w-4 h-4 text-[var(--brand-color)]" />
              </button>
            </div>

            <nav
              className="flex-1 flex flex-col justify-center px-6 gap-1"
              aria-label="Page index"
            >
              {INDEX.map((item, i) => (
                <motion.a
                  key={item.id}
                  href={`#${item.id}`}
                  onClick={() => setMenuOpen(false)}
                  className={`flex items-baseline gap-4 py-3 border-b border-[var(--border-color)] ${
                    active === item.id
                      ? "text-[var(--brand-color)]"
                      : "text-[var(--text-primary)]"
                  }`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.08 + i * 0.05, duration: 0.35 }}
                >
                  <span className="font-mono text-xs text-[var(--brand-color)]">
                    {item.num}
                  </span>
                  <span className="font-display text-2xl font-medium">
                    {item.label}
                  </span>
                </motion.a>
              ))}
            </nav>

            <motion.div
              className="px-6 pb-10 space-y-3"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.45, duration: 0.35 }}
            >
              <p className="font-mono text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">
                <span className="pulse-dot inline-block w-2 h-2 rounded-full bg-[var(--brand-color)] mr-2" />
                Taking new projects
              </p>
              <a
                href="#contact"
                onClick={() => setMenuOpen(false)}
                className="inline-flex items-center justify-center gap-2 w-full bg-[var(--brand-color)] text-[var(--bg-primary)] font-semibold rounded-sm px-7 py-3.5"
              >
                Start a project
              </a>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="lg:grid lg:grid-cols-[300px_1fr]">
        {/* Desktop index rail */}
        <aside className="hidden lg:flex flex-col justify-between sticky top-0 h-screen border-r border-[var(--border-color)] px-8 py-10">
          <div>
            <Link href="/" className="block mb-14">
              <span className="font-display text-2xl font-semibold tracking-tight">
                behes
              </span>
              <span className="font-mono text-sm text-[var(--brand-color)]">
                .dev
              </span>
            </Link>

            <p className="font-mono text-xs uppercase tracking-[0.18em] text-[var(--text-muted)] mb-4">
              Index
            </p>
            <nav aria-label="Page index">
              {INDEX.map((item) => (
                <a key={item.id} href={`#${item.id}`} className={railLink(item)}>
                  <span>{item.num}</span>
                  <span>{item.label}</span>
                </a>
              ))}
            </nav>
          </div>

          <div className="space-y-4">
            <p className="font-mono text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">
              <span className="pulse-dot inline-block w-2 h-2 rounded-full bg-[var(--brand-color)] mr-2" />
              Taking new projects
            </p>
            <p className="font-mono text-xs text-[var(--text-muted)] leading-relaxed">
              Arthur Behesnilian
              <br />
              Web designer, Pasadena CA
            </p>
            <a
              href="#contact"
              className="u-link inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.16em] text-[var(--brand-color)]"
            >
              Start a project <ArrowDown className="w-3 h-3" />
            </a>
          </div>
        </aside>

        {/* Document column: min-w-0 stops the max-content marquee from
            inflating the 1fr grid track (grid items default to min-content) */}
        <main className="min-w-0 overflow-x-clip px-5 sm:px-10 xl:px-20 pb-24">
          {/* 00 Intro */}
          <section className="relative min-h-[92vh] flex flex-col justify-center py-20 overflow-hidden -mx-5 sm:-mx-10 xl:-mx-20 px-5 sm:px-10 xl:px-20">
            <div className="aurora" aria-hidden="true" />

            <motion.p className="eyebrow mb-8 relative" {...fadeUp(0.05)}>
              <span className="text-[var(--brand-color)]">00</span> · Working
              file, updated 2026
            </motion.p>

            <h1
              className="font-display font-medium leading-[1.03] tracking-tight max-w-5xl mb-10 relative"
              style={{ fontSize: "clamp(2.75rem, 6.5vw, 6.25rem)" }}
            >
              <motion.span className="block" {...fadeUp(0.15)}>
                Custom websites for
              </motion.span>
              <motion.span className="block" {...fadeUp(0.28)}>
                businesses that{" "}
                <em className="italic text-[var(--brand-color)]">
                  outgrew their template.
                </em>
              </motion.span>
            </h1>

            <motion.div className="max-w-xl relative" {...fadeUp(0.45)}>
              <p className="text-lg text-[var(--text-secondary)] leading-relaxed mb-8">
                I'm Arthur. I design and code every site by hand, with frontier
                AI models working alongside me, so you get agency-level work at
                one-designer speed and price. The work, the rates, and the
                process are all on this page.
              </p>
              <div className="flex flex-wrap gap-4">
                <a
                  href="#work"
                  className="glow-button inline-flex items-center gap-2 bg-[var(--brand-color)] text-[var(--bg-primary)] font-semibold rounded-sm px-7 py-3.5 hover:bg-[var(--brand-hover)] transition-colors"
                >
                  See the work <ArrowDown className="w-4 h-4" />
                </a>
                <a
                  href="#pricing"
                  className="inline-flex items-center gap-2 border border-[var(--border-light)] rounded-sm px-7 py-3.5 font-mono text-xs uppercase tracking-[0.16em] text-[var(--text-secondary)] hover:border-[var(--brand-color)] hover:text-[var(--brand-color)] transition-colors duration-300"
                >
                  Check the rates
                </a>
              </div>
            </motion.div>
          </section>

          {/* Marquee strip */}
          <motion.div
            className="border-y border-[var(--border-color)] py-4 overflow-hidden -mx-5 sm:-mx-10 xl:-mx-20"
            initial={{ opacity: 0.001 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.7, duration: 0.6 }}
            aria-hidden="true"
          >
            <div className="marquee-track">
              {[0, 1].map((copy) => (
                <div key={copy} className="flex shrink-0">
                  {MARQUEE.map((item) => (
                    <span
                      key={`${copy}-${item}`}
                      className="flex items-center font-mono text-xs uppercase tracking-[0.22em] text-[var(--text-muted)] px-6"
                    >
                      {item}
                      <span className="ml-12 text-[var(--brand-color)]">✦</span>
                    </span>
                  ))}
                </div>
              ))}
            </div>
          </motion.div>

          {/* 01 Work */}
          <section id="work" className="relative py-20 scroll-mt-24">
            <span className="ghost-num" aria-hidden="true">
              01
            </span>
            <SectionHeading
              num="01"
              title="Selected Work"
              note="Real sites for real businesses, each designed and coded from scratch."
            />
            <div className="relative z-10">
              {sortedProjects.map((project, i) => (
                <motion.article
                  key={project.id || project.title}
                  className="group grid md:grid-cols-12 gap-6 py-10 md:px-4 md:-mx-4 border-b border-[var(--border-color)] items-center hover:bg-[var(--bg-secondary)]/70 transition-colors duration-300"
                  {...reveal}
                >
                  <span className="md:col-span-1 font-mono text-sm text-[var(--text-muted)] group-hover:text-[var(--brand-color)] transition-colors duration-300">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="md:col-span-6">
                    <h3 className="font-display text-2xl md:text-3xl font-medium mb-3 group-hover:text-[var(--brand-light)] transition-colors duration-300">
                      {project.title}
                    </h3>
                    <p className="text-[var(--text-secondary)] text-sm leading-relaxed max-w-lg mb-4">
                      {project.description}
                    </p>
                    <div className="flex flex-wrap gap-5">
                      <a
                        href={project.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="u-link inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.14em] text-[var(--brand-color)]"
                      >
                        Visit site <ArrowUpRight className="w-3.5 h-3.5" />
                      </a>
                      {project.caseStudySlug && (
                        <Link
                          href={`/case-studies/${project.caseStudySlug}`}
                          className="u-link inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-[0.14em] text-[var(--text-muted)] hover:text-[var(--text-primary)]"
                        >
                          Read case study
                        </Link>
                      )}
                    </div>
                  </div>
                  <div className="md:col-span-5">
                    <a
                      href={project.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="block aspect-[2/1] border border-[var(--border-color)] group-hover:border-[var(--brand-color)]/50 rounded-sm overflow-hidden bg-[var(--card-gradient-from)] transition-colors duration-300"
                    >
                      <Image
                        src={project.image || "/test-mockup.webp"}
                        alt={project.alt}
                        width={1800}
                        height={900}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                        priority={i < 2}
                        loading={i < 2 ? undefined : "lazy"}
                      />
                    </a>
                  </div>
                </motion.article>
              ))}
            </div>
          </section>

          {/* 02 Rates */}
          <section id="pricing" className="relative py-20 scroll-mt-24">
            <span className="ghost-num" aria-hidden="true">
              02
            </span>
            <SectionHeading
              num="02"
              title="Rates"
              note="The actual rate card. Fixed quotes, no surprises, and you own everything at the end."
            />
            <div className="relative z-10 border-t border-[var(--border-color)]">
              {packages.map((service, i) => (
                <motion.div
                  key={service.id || service.title}
                  className="group grid md:grid-cols-12 gap-3 md:gap-6 py-8 md:px-4 md:-mx-4 border-b border-[var(--border-color)] hover:bg-[var(--bg-secondary)]/70 transition-colors duration-300"
                  {...reveal}
                >
                  <span className="md:col-span-1 font-mono text-sm text-[var(--text-muted)] group-hover:text-[var(--brand-color)] transition-colors duration-300">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <div className="md:col-span-7">
                    <h3 className="font-display text-2xl font-medium mb-2">
                      {service.title}
                    </h3>
                    <p className="text-[var(--text-secondary)] text-sm leading-relaxed max-w-lg mb-3">
                      {service.description}
                    </p>
                    {Array.isArray(service.features) &&
                      service.features.length > 0 && (
                        <p className="font-mono text-xs text-[var(--text-muted)] leading-relaxed">
                          {service.features.join(" · ")}
                        </p>
                      )}
                  </div>
                  <div className="md:col-span-4 md:text-right">
                    <p className="font-display text-2xl md:text-3xl text-[var(--text-primary)] group-hover:text-[var(--brand-light)] transition-colors duration-300">
                      {formatPrice(service)}
                    </p>
                    {service.targetMessage && (
                      <p className="font-mono text-xs text-[var(--text-muted)] mt-2">
                        {service.targetMessage}
                      </p>
                    )}
                  </div>
                </motion.div>
              ))}
              {support.map((service) => (
                <motion.div
                  key={service.id || service.title}
                  className="group grid md:grid-cols-12 gap-3 md:gap-6 py-6 md:px-4 md:-mx-4 border-b border-[var(--border-color)] hover:bg-[var(--bg-secondary)]/70 transition-colors duration-300"
                  {...reveal}
                >
                  <span className="md:col-span-1 font-mono text-sm text-[var(--brand-color)]">
                    +
                  </span>
                  <div className="md:col-span-7">
                    <h3 className="font-display text-xl font-medium mb-1">
                      {service.title}
                    </h3>
                    <p className="text-[var(--text-secondary)] text-sm max-w-lg">
                      {service.description}
                    </p>
                  </div>
                  <div className="md:col-span-4 md:text-right">
                    <p className="font-display text-xl text-[var(--text-primary)]">
                      {formatPrice(service)}
                      {service.recurringPeriod ? (
                        <span className="font-mono text-xs text-[var(--text-muted)]">
                          {" "}
                          / {service.recurringPeriod}
                        </span>
                      ) : null}
                    </p>
                  </div>
                </motion.div>
              ))}
            </div>
            <p className="mt-6 font-mono text-xs text-[var(--text-muted)] leading-relaxed relative z-10">
              Every project includes mobile optimization, fast loading, SEO
              fundamentals, and a guaranteed launch date.
            </p>
          </section>

          {/* 03 Process & AI */}
          <section id="process" className="relative py-20 scroll-mt-24">
            <span className="ghost-num" aria-hidden="true">
              03
            </span>
            <SectionHeading
              num="03"
              title="Process & AI"
              note="Four steps from first call to launch, with frontier AI models doing the heavy lifting and a human making every call."
            />
            <div className="relative z-10 grid md:grid-cols-2 gap-px bg-[var(--border-color)] border border-[var(--border-color)] rounded-sm overflow-hidden mb-16">
              {PROCESS.map((item, i) => (
                <motion.div
                  key={item.step}
                  className="bg-[var(--bg-primary)] p-8 hover:bg-[var(--bg-secondary)] transition-colors duration-300"
                  {...reveal}
                >
                  <p className="font-mono text-xs text-[var(--brand-color)] mb-3">
                    Step {i + 1}
                  </p>
                  <h3 className="font-display text-xl font-medium mb-2">
                    {item.step}
                  </h3>
                  <p className="text-[var(--text-secondary)] text-sm leading-relaxed">
                    {item.detail}
                  </p>
                </motion.div>
              ))}
            </div>

            <motion.h3
              className="relative z-10 font-display font-medium tracking-tight mb-8"
              style={{ fontSize: "clamp(1.5rem, 3vw, 2.25rem)" }}
              {...reveal}
            >
              One designer,{" "}
              <em className="italic text-[var(--brand-color)]">
                a studio's worth
              </em>{" "}
              of frontier models.
            </motion.h3>
            <div className="relative z-10 border-t border-[var(--border-color)]">
              {AI_STACK.map((row, i) => (
                <motion.div
                  key={row.task}
                  className="group grid md:grid-cols-12 gap-2 md:gap-6 py-5 md:px-4 md:-mx-4 border-b border-[var(--border-color)] items-baseline hover:bg-[var(--bg-secondary)]/70 transition-colors duration-300"
                  {...reveal}
                >
                  <span className="md:col-span-1 font-mono text-xs text-[var(--brand-color)]">
                    0{i + 1}
                  </span>
                  <p className="md:col-span-4 font-medium text-[var(--text-primary)]">
                    {row.task}
                  </p>
                  <p className="md:col-span-3 font-mono text-sm">
                    {row.model}{" "}
                    <span className="text-xs text-[var(--text-muted)] uppercase tracking-[0.12em]">
                      {row.maker}
                    </span>
                  </p>
                  <p className="md:col-span-4 text-sm text-[var(--text-secondary)]">
                    {row.detail}
                  </p>
                </motion.div>
              ))}
            </div>
          </section>

          {/* 04 Clients */}
          {testimonials.length > 0 && (
            <section id="clients" className="relative py-20 scroll-mt-24">
              <span className="ghost-num" aria-hidden="true">
                04
              </span>
              <SectionHeading num="04" title="Clients" note="In their own words." />
              <div className="relative z-10 space-y-16">
                {testimonials.map((t) => (
                  <motion.blockquote key={t.id || t.name} {...reveal}>
                    <span
                      className="block font-display text-[var(--brand-color)] leading-none select-none"
                      style={{ fontSize: "3rem" }}
                      aria-hidden="true"
                    >
                      "
                    </span>
                    <p
                      className="font-display font-medium leading-snug tracking-tight max-w-3xl mb-5 -mt-4"
                      style={{ fontSize: "clamp(1.35rem, 2.6vw, 2rem)" }}
                    >
                      {t.content}
                    </p>
                    <footer className="font-mono text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">
                      <span className="text-[var(--brand-color)] mr-2">✦</span>
                      {t.name}
                      {t.company ? ` · ${t.company}` : ""}
                      {t.url && (
                        <>
                          {" "}
                          ·{" "}
                          <a
                            href={t.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="u-link text-[var(--brand-color)]"
                          >
                            visit site ↗
                          </a>
                        </>
                      )}
                    </footer>
                  </motion.blockquote>
                ))}
              </div>
            </section>
          )}

          {/* 05 About */}
          <section id="about" className="relative py-20 scroll-mt-24">
            <span className="ghost-num" aria-hidden="true">
              05
            </span>
            <SectionHeading num="05" title="About" />
            <div className="relative z-10 grid md:grid-cols-12 gap-10">
              <motion.div className="md:col-span-7" {...reveal}>
                <p
                  className="font-display font-medium leading-snug tracking-tight mb-6"
                  style={{ fontSize: "clamp(1.5rem, 3vw, 2.25rem)" }}
                >
                  Born and raised in Pasadena. I build websites the way they
                  used to be built,{" "}
                  <em className="italic text-[var(--brand-color)]">
                    by a person who cares,
                  </em>{" "}
                  with tools that did not exist two years ago.
                </p>
                <p className="text-[var(--text-secondary)] leading-relaxed max-w-xl">
                  Template sites all look the same because they all start from
                  the same file. I start from your business. Hand-written code
                  keeps the sites fast and fully yours, and directing frontier
                  AI models keeps a one-person operation moving at the pace of
                  a team. You always work directly with me, from first call to
                  launch.
                </p>
              </motion.div>
              <motion.div className="md:col-span-5" {...reveal}>
                <dl className="border-t border-[var(--border-color)]">
                  {[
                    ["Name", "Arthur Behesnilian"],
                    ["Based in", "Pasadena, California"],
                    ["Serving", "Los Angeles & nationwide"],
                    ["Builds with", "Next.js, React, custom code"],
                    ["Directs", "Claude · GPT · Gemini"],
                    ["Replies within", "24 hours"],
                  ].map(([term, def]) => (
                    <div
                      key={term}
                      className="flex justify-between gap-4 py-3 border-b border-[var(--border-color)]"
                    >
                      <dt className="font-mono text-xs uppercase tracking-[0.16em] text-[var(--text-muted)]">
                        {term}
                      </dt>
                      <dd className="text-sm text-[var(--text-primary)] text-right">
                        {def}
                      </dd>
                    </div>
                  ))}
                </dl>
              </motion.div>
            </div>
          </section>

          {/* 06 FAQ */}
          <section id="faq" className="relative py-20 scroll-mt-24">
            <span className="ghost-num" aria-hidden="true">
              06
            </span>
            <SectionHeading num="06" title="FAQ" />
            <div className="relative z-10 border-t border-[var(--border-color)] max-w-3xl">
              {FAQ_ITEMS.map((item) => (
                <motion.details
                  key={item.q}
                  className="group border-b border-[var(--border-color)]"
                  {...reveal}
                >
                  <summary className="flex items-center justify-between gap-4 py-5 cursor-pointer list-none font-display text-lg font-medium marker:content-none hover:text-[var(--brand-light)] transition-colors duration-300">
                    {item.q}
                    <span className="font-mono text-[var(--brand-color)] transition-transform duration-300 group-open:rotate-45">
                      +
                    </span>
                  </summary>
                  <p className="pb-6 text-[var(--text-secondary)] text-sm leading-relaxed max-w-2xl">
                    {item.a}
                  </p>
                </motion.details>
              ))}
            </div>
          </section>

          {/* 07 Contact */}
          <section id="contact" className="relative py-20 scroll-mt-24 overflow-hidden">
            <span className="ghost-num" aria-hidden="true">
              07
            </span>
            <div
              className="absolute -bottom-48 left-1/3 w-[36rem] h-[36rem] rounded-full bg-[var(--brand-color)]/8 blur-[120px] pointer-events-none"
              aria-hidden="true"
            />
            <SectionHeading
              num="07"
              title="Start a Project"
              note="Tell me about your business and I will tell you exactly what I would build, how long it would take, and what it would cost. Free, no commitment."
            />
            <div className="relative z-10 max-w-2xl">
              <ContactPanel services={services} />
            </div>
          </section>

          {/* Footer strip */}
          <footer className="pt-10 border-t border-[var(--border-color)] flex flex-wrap items-center justify-between gap-4">
            <p className="font-mono text-xs text-[var(--text-muted)]">
              behes.dev © 2026 · Arthur Behesnilian · Pasadena, CA
            </p>
            <nav className="flex gap-6 font-mono text-xs uppercase tracking-[0.14em] text-[var(--text-muted)]">
              <Link href="/privacy-policy" className="u-link hover:text-[var(--text-primary)]">
                Privacy
              </Link>
              <a href="#top" className="u-link hover:text-[var(--text-primary)]">
                Back to top
              </a>
            </nav>
          </footer>
        </main>
      </div>
    </div>
  );
}
